import { useLocale as useLocaleR42 } from '@/lib/locale';
/**
 * TravelRequestDialog — Phase 136 (Travel Smart fallback).
 *
 * "None of these times work" → the family tells the coach where they live
 * and when they're free; the coach gets a bell notification + a Leads row
 * and can shuffle their route. Two modes share one form:
 *
 *   mode="portal" → POST /api/parent/coach/:slug/travel-request   (logged-in, approved parent)
 *   mode="public" → POST /api/public/coach/:slug/travel-request   (website visitor; asks for name/email)
 *
 * `address` pre-fills from the portal profile / public widget input.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Route, Send, CheckCircle2 } from 'lucide-react';

const DAYS = [
    ['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun'],
];

export function TravelRequestDialog({
    open,
    onOpenChange,
    slug,
    coachName = 'your coach',
    mode = 'portal',
    address = '',
    accentColor = '#00d4ff',
    onSent,
}) {
    const { t: t42 } = useLocaleR42();
    const [days, setDays] = useState([]);
    const [times, setTimes] = useState('');
    const [message, setMessage] = useState('');
    const [addr, setAddr] = useState(address || '');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [students, setStudents] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        if (open) {
            setAddr(address || '');
            setSent(false);
        }
    }, [open, address]);

    const toggleDay = (d) => setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));

    const submit = async () => {
        if (mode === 'public') {
            if (name.trim().length < 2) { toast.error(t42("Please enter your name.")); return; }
            if (!email.includes('@')) { toast.error(t42("Please enter a valid email.")); return; }
        }
        if (addr.trim().length < 6) { toast.error(t42("Please enter your street address so the coach can plan the drive.")); return; }
        setSending(true);
        try {
            const body = { preferred_days: days, preferred_times: times.trim(), message: message.trim(), address: addr.trim() };
            if (mode === 'public') {
                await api.post(`/public/coach/${slug}/travel-request`, { ...body, name: name.trim(), email: email.trim(), phone: phone.trim(), students: students.trim() });
            } else {
                await api.post(`/parent/coach/${slug}/travel-request`, body);
            }
            setSent(true);
            onSent?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not send your request. Please try again.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg" data-testid="travel-request-dialog">
                <DialogHeader className="border-b border-[#0a1628]/10 px-6 py-5">
                    <DialogTitle className="flex items-center gap-2 font-display text-xl font-bold text-[#0a1628]">
                        <Route className="h-5 w-5" style={{ color: accentColor }} /> {t42("Request lessons at your home")} </DialogTitle>
                    <DialogDescription className="text-sm text-[#1a3a5c]/80">
                        {sent
                            ? t42("Sent! Your request is in the coach’s inbox.")
                            : `Tell ${coachName} where you are and when works best. They\u2019ll look at their route and reach out with options.`}
                    </DialogDescription>
                </DialogHeader>

                {sent ? (
                    <div className="flex flex-col items-center px-6 py-10 text-center" data-testid="travel-request-sent">
                        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#16a34a]/10 text-[#166534]">
                            <CheckCircle2 className="h-7 w-7" />
                        </span>
                        <h3 className="mt-4 font-display text-lg font-bold text-[#0a1628]">{t42("Request sent")}</h3>
                        <p className="mt-1 max-w-sm text-sm text-[#1a3a5c]/80">
                            {coachName} {t42("got a notification with your address and preferred times. Most coaches reply within a day.")} </p>
                        <Button onClick={() => onOpenChange(false)} className="sd-cta sd-focus mt-6 h-10 rounded-xl px-5" data-testid="travel-request-done">{t42("Done")}</Button>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
                            {mode === 'public' && (
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="tr-name" className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Your name")}</Label>
                                        <Input id="tr-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t42("Jordan Rivera")} className="h-10 rounded-xl" data-testid="travel-request-name" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="tr-email" className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Email")}</Label>
                                        <Input id="tr-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t42("you@example.com")} className="h-10 rounded-xl" data-testid="travel-request-email" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="tr-phone" className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Phone")} <span className="font-normal normal-case opacity-70">{t42("(optional)")}</span></Label>
                                        <Input id="tr-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-0100" className="h-10 rounded-xl" data-testid="travel-request-phone" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="tr-students" className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Swimmers")} <span className="font-normal normal-case opacity-70">{t42("(names & ages)")}</span></Label>
                                        <Input id="tr-students" value={students} onChange={(e) => setStudents(e.target.value)} placeholder={t42("Ava (4), Max (6)")} className="h-10 rounded-xl" data-testid="travel-request-students" />
                                    </div>
                                </div>
                            )}
                            <div className="space-y-1.5">
                                <Label htmlFor="tr-address" className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Lesson address")}</Label>
                                <Input id="tr-address" value={addr} onChange={(e) => setAddr(e.target.value)} placeholder={t42("123 Main St, City, ST 12345")} className="h-10 rounded-xl" data-testid="travel-request-address" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Days that work")}</Label>
                                <div className="flex flex-wrap gap-1.5" role="group" aria-label={t42("Preferred days")} data-testid="travel-request-days">
                                    {DAYS.map(([k, label]) => {
                                        const on = days.includes(k);
                                        return (
                                            <button
                                                key={k}
                                                type="button"
                                                aria-pressed={on}
                                                onClick={() => toggleDay(k)}
                                                className={`sd-focus h-9 min-w-[3rem] rounded-lg border px-3 text-sm font-semibold transition-colors ${on ? 'border-[#0a1628] bg-[#0a1628] text-white' : 'border-[#0a1628]/15 bg-white text-[#1a3a5c] hover:bg-[#0a1628]/5'}`}
                                                data-testid={`travel-request-day-${k}`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="tr-times" className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Times that work")}</Label>
                                <Input id="tr-times" value={times} onChange={(e) => setTimes(e.target.value)} placeholder={t42("e.g. weekdays after 4pm, Saturday mornings")} className="h-10 rounded-xl" data-testid="travel-request-times" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="tr-message" className="text-xs font-semibold uppercase tracking-wider text-[#1a3a5c]/70">{t42("Anything else?")} <span className="font-normal normal-case opacity-70">{t42("(optional)")}</span></Label>
                                <Textarea id="tr-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder={t42("Pool details, gate code, kids' experience level…")} className="rounded-xl" data-testid="travel-request-message" />
                            </div>
                        </div>
                        <DialogFooter className="border-t border-[#0a1628]/10 px-6 py-4">
                            <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-10 rounded-xl" data-testid="travel-request-cancel">{t42("Cancel")}</Button>
                            <Button onClick={submit} disabled={sending} className="sd-cta sd-focus h-10 rounded-xl px-5" data-testid="travel-request-submit">
                                {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} {t42("Send request")} </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default TravelRequestDialog;
